import axios from 'axios';
import { clearSession, getAuthHeaders, getPreferredStartRoute, hasModulo } from '../auth/session';

function normalizeBaseUrl(value) {
  const raw = String(value || '').trim();
  return raw ? raw.replace(/\/+$/, '') : '';
}

function resolveApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const runtimeValue = normalizeBaseUrl(window.__APP_CONFIG__?.API_BASE_URL);
    if (runtimeValue) return runtimeValue;
  }

  return normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL);
}

function isExternalAbsoluteUrl(url) {
  const raw = String(url || '').trim();
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const target = new URL(raw);
    return target.origin !== window.location.origin;
  } catch (_) {
    return false;
  }
}

const apiBaseUrl = resolveApiBaseUrl();
if (apiBaseUrl) {
  axios.defaults.baseURL = apiBaseUrl;
}

axios.interceptors.request.use((config) => {
  // Nao injeta auth headers para APIs externas (ex.: ViaCEP/BrasilAPI).
  // Isso evita preflight/CORS desnecessario.
  if (isExternalAbsoluteUrl(config?.url)) {
    return config;
  }

  const headers = getAuthHeaders();
  config.headers = {
    ...(config.headers || {}),
    ...headers,
  };
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const code = error?.response?.data?.code;
    const currentPath = window.location.pathname || '';
    const integrationFinanceUrl = url.includes('/api/construtor/contas-contabeis') || url.includes('/api/construtor/centros-custo');
    const integrationFinancePath =
      currentPath.startsWith('/cadastros/construtor-contas-contabeis') ||
      currentPath.startsWith('/cadastros/construtor-centros-custo');
    const collectionsEngineUrl = url.includes('/api/collections');
    const collectionsEnginePath = currentPath.startsWith('/cobrancas/recuperacao');
    const preferredRoute = getPreferredStartRoute();
    const homeEntryTargets = [
      preferredRoute && preferredRoute !== '/login' ? preferredRoute : null,
      hasModulo('financeiro') ? '/dashboard' : null,
      hasModulo('solar') ? '/solar/dashboard' : null,
      hasModulo('construtor') ? '/construtor' : null,
      hasModulo('radar_lucro') ? '/radar-lucro' : null,
    ].filter(Boolean);
    const isHomeEntryTarget = homeEntryTargets.includes(currentPath);

    if (status === 401 && !url.includes('/api/auth/login')) {
      clearSession();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (status === 403 && (code === 'MODULO_NAO_HABILITADO' || code === 'CAPACIDADE_NAO_HABILITADA' || code === 'ASSINATURA_BLOQUEADA')) {
      // Esses dois cadastros podem ser usados por empresas somente com modulo financeiro.
      // Evita redirecionamento global em caso de bloqueio indevido e deixa a pagina tratar o erro.
      if (integrationFinanceUrl || integrationFinancePath || collectionsEngineUrl || collectionsEnginePath) {
        return Promise.reject(error);
      }
      // Evita loop de navegacao: "/" -> HomeEntry -> rota inicial do modulo -> 403 -> "/".
      if (currentPath === '/' || isHomeEntryTarget) {
        return Promise.reject(error);
      }
      if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
        window.location.href = '/';
      }
      // Evita cascata de toasts de erro enquanto redireciona.
      return new Promise(() => {});
    }

    if (status === 403 && code === 'PERFIL_SEM_PERMISSAO') {
      // Mantem na tela atual para UX de "somente leitura" nas paginas.
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axios;
